import { useEffect } from "react";
import * as Team from "src/state/modules/team";
import { useLiveScoreboard } from "src/components/util/useLiveScoreboard";
import { publishScoreboard } from "src/state/modules/cast-scoreboard";
import Styles from "./LiveScoreboard.module.scss";

// A compact live scoreboard (both scores, inning + top/bottom, outs) overlaid on the live player.
// It also publishes the board to the Cast bridge so a custom Cast receiver can draw it on the TV.
// Renders nothing until there's live game data, so it's invisible for non-live / pre-first-pitch.
export default function LiveScoreboard({ team, eventId }: { team: Team.Team; eventId: string }) {
  const sb = useLiveScoreboard(team, eventId);

  // Mirror the browser board to the TV (no-op unless a custom-receiver cast session is active).
  useEffect(() => {
    publishScoreboard(sb);
    return () => publishScoreboard(null);
  }, [sb]);

  if (!sb) return null;

  const rows: { side: "away" | "home"; name: string; score: number }[] = [
    { side: "away", name: sb.away.name, score: sb.away.score },
    { side: "home", name: sb.home.name, score: sb.home.score },
  ];

  return (
    <div className={Styles.scoreboard} aria-hidden="true">
      <div className={Styles.teams}>
        {rows.map((row) => (
          <div key={row.side} className={Styles.row}>
            <span className={sb.batting === row.side ? `${Styles.bat} ${Styles.batOn}` : Styles.bat} />
            <span className={Styles.name}>{row.name}</span>
            <span className={Styles.score}>{row.score}</span>
          </div>
        ))}
      </div>
      <div className={Styles.status}>
        <span className={Styles.inning}>
          <span className={Styles.arrow}>{sb.half === "top" ? "▲" : "▼"}</span>
          {sb.inning}
        </span>
        <span className={Styles.outs}>
          {[0, 1, 2].map((i) => (
            <span key={i} className={i < sb.outs ? `${Styles.outDot} ${Styles.outDotOn}` : Styles.outDot} />
          ))}
          <span className={Styles.outsLabel}>{sb.outs} out</span>
        </span>
      </div>
    </div>
  );
}
